import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../utils/axios.js";
import { Pill, Calendar, Clock, FileText, AlertCircle, Package, Heart } from 'lucide-react';
import Footer from "../../components/Footer";
import "./medicine.scss";

const MedicineMainContainer = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [latestMedicines, setLatestMedicines] = useState([]);
  const [pastMedicines, setPastMedicines] = useState([]);

  useEffect(() => {
    // データを一度だけ取得
    let isMounted = true;

    const fetchMedicines = async () => {
      try {
        const response = await axiosInstance.get(`/medicines`, {
          headers: {
            uid: props.token,
            "Access-Control-Allow-Origin": "*",
          },
        });
        
        if (isMounted) {
          setLatestMedicines(groupByReservation(response.data.latest_medicines));
          setPastMedicines(groupByReservation(response.data.past_medicines));
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching medicines:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMedicines();

    // クリーンアップ関数
    return () => {
      isMounted = false;
    };
  }, []); // 空の依存配列で一度だけ実行

  const groupByReservation = (medicines) => {
    return medicines.reduce((result, item) => {
      const key = `${item.start_time}_${item.subject}`;
      if (!result[key]) {
        result[key] = {
          start_time: item.start_time,
          subject: item.subject,
          medicines: [],
        };
      }
      result[key].medicines.push(item);
      return result;
    }, {});
  };

  // 画像のソースを取得する関数
  const getImageSrc = (item) => {
    // ActiveStorageの画像URLがある場合はそれを使用
    if (item.image_url) {
      return item.image_url;
    }
    // なければnullを返す（画像なし）
    return null;
  };

  if (isLoading) {
    return (
      <div>
        <div className="loader" style={{ width: "100px", height: "100px" }}></div>
        <div
          style={{
            width: "100%",
            position: "absolute",
            bottom: "0%",
            height: "80px",
            background: "#FEFBF9",
            zIndex: "12",
          }}
        />
      </div>
    );
  }

  return (
    <div className="sunny_container">
      <div className="prescription_history_page">
        {/* 最新の処方 */}
        <section className="prescription_section">
          <div className="prescription_section_header">
            <Pill className="prescription_section_icon latest" />
            <h3 className="prescription_section_title">最新の処方内容</h3>
          </div>
          
          {Object.keys(latestMedicines).length === 0 ? (
            <div className="prescription_empty_state">
              <Package className="empty_prescription_icon" />
              <p className="empty_prescription_title">処方履歴がありません</p>
              <p className="empty_prescription_subtitle">まずは診察を受けてみましょう</p>
            </div>
          ) : (
            <div className="prescription_groups_container">
              {Object.values(latestMedicines).map((group, index) => (
                <div key={index} className="prescription_group_card latest">
                  <div className="prescription_consultation_info">
                    <div className="consultation_date_container">
                      <Calendar className="consultation_icon" />
                      <div className="consultation_details">
                        <span className="consultation_date">20{group.start_time}</span>
                      </div>
                    </div>
                    <div className="consultation_subject">
                      <Heart className="subject_icon" />
                      <span className="subject_name">{group.subject}</span>
                    </div>
                  </div>
                  
                  <div className="prescription_medicines_list">
                    {group.medicines.map((item) => (
                      <div className="prescription_medicine_card" key={item.id}>                        
                        <div className="prescription_medicine_content">
                          <div className="medicine_image_container">
                            {getImageSrc(item) ? (
                              <img
                                src={getImageSrc(item)}
                                alt={item.title}
                                className="prescription_medicine_image"
                              />
                            ) : (
                              <div className="no-image-placeholder">画像なし</div>
                            )}
                          </div>
                          <h4 className="prescription_medicine_title" style={{textAlign:'center'}}>{item.title}</h4>
                          
                          <div className="prescription_medicine_details">
                            <div className="medicine_detail_item">
                              <span className="detail_label">効能:</span>
                              <span className="detail_value">{item.effects}</span>
                            </div>
                            
                            <div className="medicine_detail_item">
                              <span className="detail_label">副作用:</span>
                              <span className="detail_value">{item.precautions}</span>
                            </div>
                            
                            <div className="medicine_detail_item">
                              <span className="detail_label">用法用量:</span>
                              <span className="detail_value">{item.usage1}: {item.usage2}</span>
                            </div>
                            
                            <div className="medicine_detail_item dosage">
                              {item.days == 0 ? (
                                <>
                                  <span className="detail_label">個数:</span>
                                  <span className="detail_value">{item.dosage}個</span>
                                </>
                              ) : (
                                <>
                                  <span className="detail_label">錠数:</span>
                                  <span className="detail_value">1日計{item.dosage}錠: {item.days}日分</span>
                                </>
                              )}
                            </div>
                            {item?.free_text && item.free_text.trim() && (
                                <div className="medicine_detail_item">
                                  <span className="detail_value">{item.free_text}</span>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 過去の処方 */}
        <section className="prescription_section">
          <div className="prescription_section_header">
            <FileText className="prescription_section_icon past" />
            <h3 className="prescription_section_title">過去の処方内容</h3>
          </div>
          
          {Object.keys(pastMedicines).length === 0 ? (
            <div className="prescription_empty_state">
              <FileText className="empty_prescription_icon" />
              <p className="empty_prescription_title">過去の処方履歴がありません</p>
            </div>
          ) : (
            <div className="prescription_groups_container">
              {Object.values(pastMedicines).map((group, index) => (
                <div key={index} className="prescription_group_card past">
                  <div className="prescription_consultation_info">
                    <div className="consultation_date_container">
                      <Calendar className="consultation_icon" />
                      <div className="consultation_details">
                        <span className="consultation_date">20{group.start_time}</span>
                      </div>
                    </div>
                    <div className="consultation_subject">
                      <Heart className="subject_icon" />
                      <span className="subject_name">{group.subject}</span>
                    </div>
                  </div>
                  
                  <div className="prescription_medicines_list">
                    {group.medicines.map((item) => (
                      <div className="prescription_medicine_card" key={item.id}>
                        <div className="prescription_medicine_content">
                        <div className="medicine_image_container">
                          {getImageSrc(item) ? (
                            <img
                              src={getImageSrc(item)}
                              alt={item.title}
                              className="prescription_medicine_image"
                            />
                          ) : (
                            <div className="no-image-placeholder">画像なし</div>
                          )}
                        </div>
                        
                          <h4 className="prescription_medicine_title">{item.title}</h4>
                          
                          <div className="prescription_medicine_details">
                            <div className="medicine_detail_item">
                              <span className="detail_label">効能:</span>
                              <span className="detail_value">{item.effects}</span>
                            </div>
                            
                            <div className="medicine_detail_item">
                              <span className="detail_label">副作用:</span>
                              <span className="detail_value">{item.precautions}</span>
                            </div>
                            
                            <div className="medicine_detail_item">
                              <span className="detail_label">用法用量:</span>
                              <span className="detail_value">{item.usage1}: {item.usage2}</span>
                            </div>
                            
                            <div className="medicine_detail_item dosage">
                              <span className="detail_label">錠数:</span>
                              <span className="detail_value">1日計{item.dosage}錠: {item.days}日分</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default MedicineMainContainer;